<template>
  <div class="point_info">
    <h3>{{ $t('common.title') }}</h3>
    <div class="deco">
      <h2>{{ pointInfo.pointName }}</h2>
      <div class="wrapper_text">
        <p class="text_point">
          {{ point }}<span class="unit">{{ pointInfo.pointUnitName }}</span>
        </p>
        <template v-if="pointInfo.pointName">
          <p class="text_expiry_point" :class="{ ja: userRegion === 'jppc' }">
            {{ expiryGuide }}
            {{ expiryPoint }}
          </p>
          <p class="text_expiry_point date">
            {{ expiryDate }}
          </p>
        </template>
        <v-button
          :text="$t('common.btnExchange')"
          class="btn08"
          iconClass="icon_change"
          @handleClick="handleClick"
          :obj="this.a2s + '_conversion'"
          :opt="`{&quot;servicename&quot;:&quot;${serviceUrl}&quot;}`"
          @click.native="handleA2S"
          ref="btn_change"
        ></v-button>
      </div>
    </div>
    <span v-if="userRegion === 'krpc'" class="more_info">
      <v-icon clsName="icon_info" />
      <em @click="openInfoModal">{{ $t('common.textInfo') }}</em>
    </span>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import VButton from '@/components/atoms/VButton';
import VIcon from '@/components/atoms/VIcon';
import mixin from '@/utils/mixin';
import { pointErrorMsg } from '@/utils/errorMsg';
import { checkUserRegion, goToLoginPage } from '@/utils';
import sendLog from '@/utils/analistic';
import eventBus from '@/utils/eventBus';
import { exchangeApi } from '@/api';

export default {
  mixins: [mixin],
  components: {
    VButton,
    VIcon,
  },
  props: {
    a2s: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      krpcNotice: '',
    };
  },
  computed: {
    ...mapGetters(['serviceUrl']),
    ...mapGetters('point', ['pointInfo']),
    ...mapGetters('userInfo', ['userInfo']),
    ...mapGetters('exchange', ['exchangeCurrency']),
    point() {
      const { point } = this.pointInfo;
      if (point === undefined) return '';
      if (point === null) return '-';

      const sign = point < 0 ? '-' : '';
      return `${sign}${this.addComma(this.pointInfo.point)}`;
    },
    userRegion() {
      return checkUserRegion();
    },
    isSettlement() {
      return this.pointInfo.expiryPoint === null && this.pointInfo.point === null;
    },
    expiryGuide() {
      // expiryPoint가 null일 경우 정산중
      if (this.isSettlement) {
        return this.$t('common.textExpiryInSettlement');
      }
      return this.userRegion === 'krpc' ? this.$t('common.textExpire') : this.$t('common.textExpireGlobal');
    },
    expiryPoint() {
      if (this.isSettlement) {
        return '';
      }

      return `${this.addComma(this.pointInfo.expiryPoint)}${this.pointInfo.pointUnitName}`;
    },
    expiryDate() {
      if (this.userRegion === 'krpc' || this.pointInfo.expiryPoint === null) {
        return '';
      }
      // 소멸 예정일은 무조건 한국 시각을 기준으로 한다.
      const krTimezone = this.$moment.tz('Asia/Seoul');
      const date = `${krTimezone.endOf('month').format('YYYY.MM.DD')}(UTC+9)`;
      return this.$i18n.t('common.textLost', { date });
    },
  },
  watch: {
    $route(to, from) {
      if (to.path !== from.path) {
        this.init();
      }
    },
  },
  created() {
    this.init();
  },
  mounted() {
    window.addEventListener('message', this.iframeEvent, false);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.iframeEvent, false);
  },
  methods: {
    ...mapActions(['setExchangeLayerUrl']),
    ...mapActions('point', ['getPointInfo']),
    ...mapActions('exchange', ['checkExchange', 'getExchangeInfo']),
    ...mapActions('modal', ['openModal']),
    ...mapActions('userInfo', ['getUserInfo']),
    async init() {
      if (this.userRegion === 'krpc') {
        await this.getUserInfo();
      }
      await this.getPointInfo();
      await this.getExchangeInfo();

      const krpcNoticeResponse = await exchangeApi.getKrpcNotice();
      const { data } = krpcNoticeResponse;
      this.krpcNotice = this.$sanitize(data.data.withdrawNotice);
    },
    async handleClick() {
      if (this.userRegion === 'krpc') {
        const apiReturn = await this.checkExchange();
        const validCode = [0, 3004, 3005, 3006, 3013, 3015, 3017];

        if (validCode.includes(apiReturn.code)) {
          // 최소 신청금액보다 보유금액이 적거나 월 전환 횟수 초과하더라도 전환 신청 페이지로 이동
          this.setExchangeLayerUrl(`/${this.$root.$i18n.locale}/${this.serviceUrl}/exchange/step1`);
        } else if (apiReturn.code !== 2) {
          this.openModal(pointErrorMsg(apiReturn.code));
        }
      } else {
        if (this.exchangeCurrency.currency === 'KRW') {
          this.openModal({
            type: 'alert',
            message: this.$t('global.notAvailableCurrency'),
          });
        } else {
          this.$router.push({ name: 'GlobalWithdrawInfo', query: { ...this.$route.query } });
        }
      }
    },
    handleA2S() {
      const attr = this.$refs.btn_change.$attrs;
      sendLog(attr.obj, JSON.parse(attr.opt));
    },
    iframeEvent({ data }) {
      if (data.event === 'iframeClose') {
        this.setExchangeLayerUrl('');
        if (this.$route.name !== 'ExchangeHistory') {
          this.$router.replace({ name: 'ExchangeHistory' });
        } else {
          this.getPointInfo();
          eventBus.$emit('listUpdate');
          // this.$router.go(this.$router.currentRoute)
        }
      }
      if (data.event === 'iframeModal') {
        this.openModal(data.data);
      }
      if (data.event === 'iframeLogin') {
        goToLoginPage();
      }
      if (data.event === 'iframeModalCertified') {
        this.openModal({
          type: 'confirm',
          message: '장시간 이용이 없어 인증이 만료되었습니다.<br/>처음부터 다시 진행해주세요.',
          callback: () => {
            eventBus.$emit('iframeReset');
          },
        });
      }
    },
    openInfoModal() {
      this.openModal({
        type: 'data',
        header: this.$t('common.pointRedemptionInfo'),
        message: this.krpcNotice,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.point_info {
  margin-bottom: 30px;
  font-family: 'NEXONLv1Gothic';
  h2,
  p {
    color: #fff;
  }
  .wrapper_text {
    display: flex;
    align-items: center;
    margin-top: 24px;
  }
  h2 {
    font-weight: 700;
    font-size: 17px;
    line-height: 20px;
  }
  .text_point {
    display: flex;
    align-items: baseline;
    font-weight: 700;
    font-size: 40px;
    line-height: 46px;
  }
  .unit {
    margin-left: 5px;
    font-weight: 400;
    font-size: 30px;
    line-height: 34px;
  }
  .text_expiry_point {
    margin-left: 15px;
    margin-top: 18px;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    font-family: 'Malgun Gothic', Arial, sans-serif;
    opacity: 0.7;
    &.ja {
      font-family: 'Noto Sans JP', 'Helvetica Neue', Arial, 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', Meiryo,
        sans-serif;
    }
    .date {
      margin-left: 15px;
    }
  }
}
h3 {
  font-weight: 400;
  font-size: 22px;
  line-height: 25px;
  color: #050c42;
  margin-bottom: 15px;
}
.deco {
  background: #050c42;
}
.more_info {
  display: inherit;
  margin-top: 10px;
  text-align: right;
  font-size: 12px;
  line-height: 1.33;
  color: #333;
  em {
    text-decoration: underline;
    cursor: pointer;
  }
}

@media all and (max-width: 767px) {
  .point_info {
    font-size: 15px;
    line-height: 17px;
    h2 {
      font-size: 15px;
      line-height: 17px;
    }
    .wrapper_text {
      display: block;
      margin-top: 24px;
      text-align: center;
    }
    .text_point {
      display: flex;
      justify-content: center;
      align-items: baseline;
      font-weight: 700;
      font-size: 40px;
      line-height: 46px;
    }
    .unit {
      margin-left: 5px;
      font-weight: 400;
      font-size: 30px;
      line-height: 34px;
    }
    .text_expiry_point {
      margin: 10px 0 30px 0;
      font-weight: 400;
      font-size: 14px;
      line-height: 19px;
      opacity: 0.7;
    }
  }
}

@media all and (max-width: 480px) {
  h3 {
    margin-left: 15px;
  }
  .deco {
    margin: 0 15px;
  }
  .more_info {
    margin: 10px 15px 0 15px;
  }
}
</style>
